import Vue from "vue"
import axios from "axios"
import lodash from "lodash"
import config from "../config/client"
import * as appDataService from "@/AppDataService"
import * as utils from "@/Shared/utils"
import {
  DEV_HOST,
  TEST_HOST,
  PROD_HOST,
  LOCALHOST,
  LOCAL,
  DEV,
  STAGING,
  MPX_SERVICE_MVATS,
  MPX_SERIVCE_OPSUI,
  MPX_SERVICE_MVATS_PREVIEW,
  MPX_SERIVCE_OPSUI_PREVIEW
} from "@/Shared/constants"

export default new Vue({
  name: "AppService",
  data: {
    localStorage: {
      mpxUserInfo: {
        type: Object
      },
      cableUserInfo: {
        type: Object
      }
    }
  },
  methods: {
    /**
     * isSignedIntoMPX - Check if user is signed in with MPX credentials
     * NOTE: This method identifies the status based on data in local storage
     *
     * @return {Promise}
     */
    isSignedIntoMPX() {
      return appDataService
        .getMPXUserInfo()
        .then(userInfo => {
          if (
            lodash.isUndefined(userInfo) ||
            lodash.isNull(userInfo) ||
            lodash.isEmpty(userInfo)
          ) {
            return Promise.reject(
              new Error("Missing MPX user information, pelase login to access.")
            )
          } else {
            return true
          }
        })
        .catch(error => {
          console.log(error)
          return Promise.reject(error)
        })
    },

    /**
     * isSignedIntoCable - Check if the user is signed in to CABLE
     * NOTE: This method identifies the status based on data in local storage
     * @return {Promise}
     */
    isSignedIntoCable() {
      return appDataService
        .getCableUserInfo()
        .then(userInfo => {
          if (
            this.$lodash.isUndefined(userInfo) ||
            this.$lodash.isEmpty(userInfo) ||
            this.$lodash.isNull(userInfo)
          ) {
            return Promise.reject(
              new Error(
                "Missing CABLE user information, pelase login to access.."
              )
            )
          } else {
            return true
          }
        })
        .catch(error => {
          console.log(error)
          return Promise.reject(error)
        })
    },

    /**
     * setUpInterceptors - Sets up interceptors to intercept AJAX requests globally
     */
    setUpInterceptors() {
      //  Request interceptor
      axios.interceptors.request.use(
        config => {
          //  Pass Authorization header to XML Backlog API
          if (config.url.indexOf("api/xml-backlog") > -1) {
            return appDataService.getCableUserInfo().then(cableUserInfo => {
              config.headers["Authorization"] =
                cableUserInfo.token_type + " " + cableUserInfo.access_token
              return config
            })
          }
          if (
            config.url.indexOf("api/xvat") > -1 ||
            config.url.indexOf("api/config-service") > -1
          ) {
            return appDataService.getCableUserInfo().then(cableUserInfo => {
              config.headers["Authorization"] =
                cableUserInfo.token_type + " " + cableUserInfo.access_token_api
              return config
            })
          } else {
            return config
          }
        },
        error => {
          // Do something with request error
          return Promise.reject(error)
        }
      )
      // Response interceptor
      axios.interceptors.response.use(
        response => {
          // Handles 401 from Pre Ingest API
          var config = response.config
          var responseData = response.data
          if (
            config.url.indexOf("api/mpx") > -1 ||
            config.url.indexOf("api/pre-ingest") > -1 ||
            config.url.indexOf("api/rules") > -1 ||
            config.url.indexOf("api/providers") > -1
          ) {
            if (
              responseData &&
              responseData.isException &&
              responseData.responseCode === 401
            ) {
              this.$emit("event:mpxInvalidToken")
            }
          }
          return response
        },
        error => {
          // Handles 401 from XML Backlog API
          var config = error.config
          if (config.url.indexOf("api/xml-backlog") > -1) {
            if (error.response.status === 401) {
              this.$emit("event:cableInvalidToken")
            } else if (
              error.response.status === 500 ||
              error.response.status === 503
            ) {
              this.$emit("event:xmlbServerError")
            }
          } else if (
            config.url.indexOf("api/pre-ingest") > -1 ||
            config.url.indexOf("api/providers") > -1 ||
            config.url.indexOf("api/rules") > -1
          ) {
            if (
              lodash.isUndefined(error.response) &&
              error.message === "Network Error"
            ) {
              // Network error
              this.$emit("event:preIngestServerError")
            } else if (error.response.status === 401) {
              // Authentication
              this.$emit("event:mpxInvalidToken")
            } else if (error.response.status >= 500) {
              // Something is wrong
              this.$emit("event:preIngestServerError")
            }
          } else if (
            config.url.indexOf("api/xvat") > -1 ||
            config.url.indexOf("/api/config-service") > -1) {
            if (
              lodash.isUndefined(error.response) &&
              error.message === "Network Error"
            ) {
              // Network error
              // TODO: Update for XVAT
              this.$emit("event:preIngestServerError")
            } else if (error.response.status === 401) {
              // Authentication
              this.$emit("event:cableInvalidToken")
            } else if (error.response.status >= 500) {
              // TODO: Update for XVAT
              // Something is wrong
              this.$emit("event:preIngestServerError")
            }
          }
          return Promise.reject(error)
        }
      )
    },

    /**
     * isFeatureVisible - Identify if a feature is visible
     * @param {string} featureName
     * @returns {boolean}
     */
    isFeatureVisible(featureName) {
      let env = "dev"
      const serverUrl = utils.getServerUrl()
      let featuresConfig = config.features
      let isVisible = true
      if (
        serverUrl.indexOf(DEV_HOST) > -1 ||
        serverUrl.indexOf(LOCALHOST) > -1
      ) {
        env = "dev"
      } else if (serverUrl.indexOf(TEST_HOST) > -1) {
        env = "test"
      } else if (serverUrl.indexOf(PROD_HOST) > -1) {
        env = "prod"
      }
      if (lodash.has(featuresConfig, featureName)) {
        isVisible = featuresConfig[featureName][env]
      }

      return isVisible
    },

    /**
     * getEnvName - returns environment name
     * @returns {sring}
     */
    getEnvName() {
      let env = ""
      const serverUrl = utils.getServerUrl()

      if (serverUrl.indexOf(LOCALHOST) > -1) {
        env = LOCAL
      } else if (serverUrl.indexOf(DEV_HOST) > -1) {
        env = DEV
      } else if (serverUrl.indexOf(TEST_HOST) > -1) {
        env = STAGING
      }
      return env
    },

    /**
     * getMvatsServiceString - returns service string
     * @returns {sring}
     */
    getMvatsServiceString() {
      let serviceString = ""
      const serverUrl = utils.getServerUrl()

      if (
        serverUrl.indexOf(LOCALHOST) > -1 ||
        serverUrl.indexOf(DEV_HOST) > -1 ||
        serverUrl.indexOf(TEST_HOST) > -1
      ) {
        serviceString = MPX_SERVICE_MVATS_PREVIEW
      } else {
        serviceString = MPX_SERVICE_MVATS
      }
      return serviceString
    },

    /**
     * getEnvName - returns service string
     * @returns {sring}
     */
    getOpsUIServiceString() {
      let serviceString = ""
      const serverUrl = utils.getServerUrl()

      if (
        serverUrl.indexOf(LOCALHOST) > -1 ||
        serverUrl.indexOf(DEV_HOST) > -1 ||
        serverUrl.indexOf(TEST_HOST) > -1
      ) {
        serviceString = MPX_SERIVCE_OPSUI_PREVIEW
      } else {
        serviceString = MPX_SERIVCE_OPSUI
      }
      return serviceString
    },

    /**
     * isPolarisAccount
     * @param {string} id
     * @returns {boolean}
     */
    isPolarisAccount(id) {
      const polarisAccounts = config.polarisAccounts
      let returnVal = false
      let index = lodash.indexOf(polarisAccounts, id)
      if (index > -1) {
        returnVal = true
      } else {
        returnVal = false
      }
      return returnVal
    },

    /**
     * hasAccessToMPXView
     * @param {*} parentAccountService
     * @param {*} viewPath
     */
    hasAccessToMPXView(parentAccountService, viewPath) {
      let mvatsServiceString = this.getMvatsServiceString()
      let opsUIServiceString = this.getOpsUIServiceString()
      let mvatsPaths = ["/metadata-rules"]
      let opsUIPaths = [
        "/pre-ingest",
        "/providers/provider-list",
        "/providers/provider-groups"
      ]
      let IsMVATSPath = lodash.indexOf(mvatsPaths, viewPath) > -1
      let IsOpsUIPath = lodash.indexOf(opsUIPaths, viewPath) > -1
      let hasMvatsAccess =
        lodash.indexOf(parentAccountService, mvatsServiceString) > -1
      let hasOPsUIAccess =
        lodash.indexOf(parentAccountService, opsUIServiceString) > -1
      if (IsMVATSPath) {
        return hasMvatsAccess
      } else if (IsOpsUIPath) {
        return hasOPsUIAccess
      } else {
        return true
      }
    },

    /**
     * getQueryItems -
     * @returns {array}
     */
    getQueryItems() {
      return [
        // {
        //   icon: "code",
        //   title: "XML Backlog",
        //   pathName: "XMLBacklog",
        //   featureName: "xmlBacklog",
        //   description: "Perform MDU for non CTS metadata",
        //   auth: "CABLE"
        // },
        // {
        //   icon: "device_hub",
        //   title: "Pre Ingest",
        //   pathName: "PreIngestHome",
        //   featureName: "preIngest",
        //   description: "Manage processing records for Polaris",
        //   auth: "MPX"
        // },
        // {
        //   icon: "settings_input_component",
        //   title: "Asset Status",
        //   pathName: "AssetStatusHome",
        //   featureName: "assetStatus",
        //   description: "Query asset status across workflows",
        //   auth: "CABLE"
        // }
      ]
    },

    /**
     * getConfigurationItems -
     * @returns {array}
     */
    getConfigurationItems() {
      return [
        // {
        //   icon: "gavel",
        //   title: "Metadata Rules",
        //   pathName: "MetadataRulesSearch",
        //   featureName: "metadataRules",
        //   auth: "MPX",
        //   description:
        //     "Manage metadata validation, transformation and augmentation rules",
        //   children: [
        //     {
        //       icon: "code",
        //       title: "Xpath Mapping",
        //       pathName: "XPathMappingHome",
        //       featureName: "metadataRules",
        //       auth: "MPX"
        //     }
        //   ]
        // },
        // {
        //   icon: "group",
        //   title: "Provider Groups",
        //   pathName: "ProviderGroupsHome",
        //   featureName: "providerGroups",
        //   description: "Manage provider groups for Polaris",
        //   auth: "MPX"
        // },
        // {
        //   icon: "account_circle",
        //   title: "Providers",
        //   pathName: "ProviderListHome",
        //   featureName: "providerList",
        //   description: "Manage providers for Polaris",
        //   auth: "MPX"
        // },
        {
          icon: "code",
          title: "Config Service",
          pathName: "ConfigServiceHome",
          featureName: "configService",
          description: "Service Configuration for SDVI",
          auth: "CABLE"
        },
        {
          icon: "gavel",
          title: "XVAT",
          pathName: "XVATRulesSearch",
          featureName: "xvatRules",
          auth: "CABLE",
          description:
            "Manage metadata validation, transformation and augmentation rules",
          children: [
            {
              icon: "code",
              title: "Xpath Mapping",
              pathName: "XVATXPathMappingHome",
              featureName: "xvatRules",
              auth: "CABLE"
            }
          ]
        }
      ]
    },
    /**
     * getSubAccountsIdsString
     * @param {array} subAccounts
     */
    getSubAccountsIdsString(subAccounts) {
      if (subAccounts.length === 0) {
        return ""
      }
      let childAccountIds = lodash.map(subAccounts, "id")
      let idNumbers = lodash.map(childAccountIds, idURL =>
        idURL.split("/").pop()
      )
      let byOwnerIdString = idNumbers.join("|")
      return byOwnerIdString
    }
  },
  created() { }
})



// WEBPACK FOOTER //
// ./src/AppService.js